import { useOperationalAverages } from '../../lib/useOperationalAverages';
import type { CustomRangeBounds, RangeKey } from '@busy/api';
import { useT } from '../../lib/i18n';
import { CollapsibleSection } from './CollapsibleSection';

// Averages tile — the per-day / per-cut / per-client small numbers a
// barber needs to spot operational patterns. Collapsible BY DEFAULT
// so the dashboard stays scannable; the trigger headline shows the
// three most-glanceable numbers so the user gets the gist without
// expanding.
//
// Layout when expanded:
//
//   PER DAY                | PER VISIT
//   ───────────────────────|──────────────────────────
//   6.2 cuts/day           | $58 per cut
//   62% walk-in            |   12% had add-ons
//   4.5h cutting/day       | 35 min per cut
//   $312 income/day        | $8 tip per cut
//                          |   73% tipped
//
//   PER CLIENT
//   $172 per client
//   18% tip rate
//
// Footnotes (the small "62% walk-in", "12% had add-ons", "73% tipped"
// lines) attach to their parent metric so the relationship is visible
// — "your $58 avg ticket includes 12% of cuts with add-ons."
//
// Range-aware: respects the dashboard's range selector + custom bounds.

export function OperationalAveragesTile({
  range,
  customBounds,
}: {
  range: RangeKey;
  customBounds: CustomRangeBounds | null;
}) {
  const t = useT();
  const { data, loading } = useOperationalAverages(range, customBounds);

  if (loading) {
    return (
      <div className="rounded-2xl border border-line bg-surface animate-pulse h-14" />
    );
  }
  // Suppress entirely when there's no data to show — picking "Today"
  // on a fresh account with zero cuts shouldn't render an empty
  // averages tile. A range with zero cuts has nothing to average.
  if (!data || data.total_cuts === 0) return null;

  // Headline — three most-glanceable numbers for the collapsed trigger.
  // Picks the per-day cuts + per-cut revenue + add-on attach (the
  // upsell signal). Skips add-on attach if data is too thin (< 5 cuts
  // in range) since 1/2 = 50% reads as misleading.
  const headlineParts: string[] = [];
  headlineParts.push(
    t('dash.averages.headline.cutsPerDay', {
      n: formatNumber(data.cuts_per_day, 1),
    }),
  );
  headlineParts.push(
    t('dash.averages.headline.revPerCut', {
      amount: formatDollars(data.revenue_per_cut_cents),
    }),
  );
  if (data.addon_attach_pct != null && data.total_cuts >= 5) {
    headlineParts.push(
      t('dash.averages.headline.addonAttach', {
        pct: data.addon_attach_pct,
      }),
    );
  }

  return (
    <CollapsibleSection
      titleKey="dash.averages.title"
      summary={headlineParts.join(' · ')}
    >
      <div className="grid grid-cols-2 gap-x-4 gap-y-4 text-[13px]">
        {/* ─── PER DAY column ─── */}
        <div className="space-y-3">
          <SectionHeader label={t('dash.averages.section.perDay')} />
          <StatLine
            label={t('dash.averages.stat.cutsPerDay')}
            value={`${formatNumber(data.cuts_per_day, 1)}`}
            // Walk-in % footnote — only when there are enough tagged
            // cuts (≥3) to call it a real ratio.
            footnote={
              data.walkin_pct != null && data.walkin_appointment_tagged >= 3
                ? t('dash.averages.footnote.walkin', {
                    pct: data.walkin_pct,
                  })
                : null
            }
          />
          <StatLine
            label={t('dash.averages.stat.hoursPerDay')}
            value={`${formatNumber(data.hours_per_day, 1)}h`}
          />
          <StatLine
            label={t('dash.averages.stat.incomePerDay')}
            value={formatDollars(data.income_per_day_cents)}
          />
        </div>

        {/* ─── PER VISIT column ─── */}
        <div className="space-y-3">
          <SectionHeader label={t('dash.averages.section.perVisit')} />
          <StatLine
            label={t('dash.averages.stat.revPerCut')}
            value={formatDollars(data.revenue_per_cut_cents)}
            // Add-on attach footnote on revenue/cut — explains what
            // "average ticket" includes by way of add-ons.
            footnote={
              data.addon_attach_pct != null && data.total_cuts >= 5
                ? t('dash.averages.footnote.addonAttach', {
                    pct: data.addon_attach_pct,
                  })
                : null
            }
          />
          <StatLine
            label={t('dash.averages.stat.minutesPerCut')}
            value={`${Math.round(data.minutes_per_cut)} min`}
          />
          <StatLine
            label={t('dash.averages.stat.tipPerCut')}
            value={formatDollars(data.tip_per_cut_cents)}
            // Tip attach footnote — % of cuts that got tipped at all.
            footnote={
              data.tip_attach_pct != null && data.total_cuts >= 5
                ? t('dash.averages.footnote.tipAttach', {
                    pct: data.tip_attach_pct,
                  })
                : null
            }
          />
        </div>

        {/* ─── PER CLIENT row — spans both columns ─── */}
        <div className="col-span-2 space-y-3 pt-2 border-t border-line">
          <SectionHeader label={t('dash.averages.section.perClient')} />
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            {data.revenue_per_client_cents != null && (
              <StatLine
                label={t('dash.averages.stat.revPerClient')}
                value={formatDollars(data.revenue_per_client_cents)}
              />
            )}
            {data.tip_rate_pct != null && (
              <StatLine
                label={t('dash.averages.stat.tipRate')}
                value={`${data.tip_rate_pct.toFixed(1).replace(/\.0$/, '')}%`}
              />
            )}
          </div>
        </div>
      </div>

      {/* Sample size disclaimer — surfaces the denominator so the
          math is verifiable. Especially helps when the range is short
          ("Today" = 1 day worked, etc.) and per-day numbers read as
          extrapolated rather than averaged. */}
      <p className="text-[11px] text-ink-500 mt-3 leading-snug">
        {data.days_worked === 1
          ? t('dash.averages.disclaimer.oneDay', { cuts: data.total_cuts })
          : t('dash.averages.disclaimer.manyDays', {
              days: data.days_worked,
              cuts: data.total_cuts,
            })}
      </p>
    </CollapsibleSection>
  );
}

function SectionHeader({ label }: { label: string }) {
  return (
    <div className="text-[10px] uppercase tracking-[0.12em] font-extrabold text-ink-500">
      {label}
    </div>
  );
}

function StatLine({
  label,
  value,
  footnote,
}: {
  label: string;
  value: string;
  footnote?: string | null;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-ink-700">{label}</span>
        <span className="font-bold tabular-nums text-ink-900 dark:text-ink-100">
          {value}
        </span>
      </div>
      {footnote && (
        <div className="text-[11px] text-ink-500 tabular-nums mt-0.5">
          {footnote}
        </div>
      )}
    </div>
  );
}

function formatDollars(cents: number): string {
  const dollars = cents / 100;
  if (dollars >= 1000) {
    return `$${(dollars / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  }
  return `$${Math.round(dollars)}`;
}

function formatNumber(n: number, decimals: number): string {
  return n.toFixed(decimals).replace(/\.0$/, '');
}
